/**
 * RED-317 PR 3d-A — the data sheet's keyboard (StudioDataSheet.vue) and its
 * clipboard format, pure so they unit-test without a browser.
 */

const isMod = (event) => event.metaKey || event.ctrlKey

/**
 * What a key does. `editing`: a cell is being typed into.
 * → { type: 'move', dRow, dCol, wrap? } | { type: 'extend', dRow, dCol } | { type: 'edge', dRow, dCol, extend }
 *   | { type: 'edit' } | { type: 'type', text } | { type: 'clear' } | { type: 'cancel' }
 *   | { type: 'copy' | 'cut' | 'all' | 'undo' | 'redo' } | null (not the sheet's)
 */
export function sheetKeyAction(event, editing) {
  const { key, shiftKey } = event
  if (editing) {
    if (key === 'Escape') return { type: 'cancel' }
    if (key === 'Enter') return { type: 'move', dRow: shiftKey ? -1 : 1, dCol: 0 }
    if (key === 'Tab') return { type: 'move', dRow: 0, dCol: shiftKey ? -1 : 1, wrap: true }
    return null
  }
  const arrows = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] }
  if (arrows[key]) {
    const [dRow, dCol] = arrows[key]
    if (isMod(event)) return { type: 'edge', dRow, dCol, extend: shiftKey }
    return shiftKey ? { type: 'extend', dRow, dCol } : { type: 'move', dRow, dCol }
  }
  if (key === 'Tab') return { type: 'move', dRow: 0, dCol: shiftKey ? -1 : 1, wrap: true }
  if (key === 'Enter') return { type: 'move', dRow: shiftKey ? -1 : 1, dCol: 0 }
  if (key === 'F2') return { type: 'edit' }
  if (key === 'Delete' || key === 'Backspace') return { type: 'clear' }
  if (isMod(event) && !event.altKey) {
    const lower = key.toLowerCase()
    if (lower === 'c') return { type: 'copy' }
    if (lower === 'x') return { type: 'cut' }
    if (lower === 'a') return { type: 'all' }
    if (lower === 'z') return { type: shiftKey ? 'redo' : 'undo' }
    if (lower === 'y') return { type: 'redo' }
    return null
  }
  if (key.length === 1 && !event.altKey) return { type: 'type', text: key }
  return null
}

/**
 * Tab-separated text (Excel, Sheets) → rows of cell texts; a trailing line
 * break is no row. A cell that starts with a quote is quoted: it may hold
 * tabs and line breaks, "" is a quote (what Excel writes for such cells).
 */
export function readTsv(text) {
  const source = String(text).replace(/\r\n?/g, '\n')
  const rows = []
  let row = []
  let cell = ''
  let quoted = false
  for (let index = 0; index < source.length; index++) {
    const ch = source[index]
    if (quoted) {
      if (ch !== '"') cell += ch
      else if (source[index + 1] === '"') {
        cell += '"'
        index++
      } else quoted = false
    } else if (ch === '"' && cell === '') quoted = true
    else if (ch === '\t') {
      row.push(cell)
      cell = ''
    } else if (ch === '\n') {
      row.push(cell)
      rows.push(row)
      row = []
      cell = ''
    } else cell += ch
  }
  if (cell !== '' || row.length) rows.push([...row, cell])
  return rows
}

/** Rows of cell texts → tab-separated text, cells holding a tab or line break quoted. */
export function writeTsv(rows) {
  return rows.map((cells) => cells.map((cell) => (/[\t\n"]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell)).join('\t')).join('\n')
}
